/**
 * S100 — Gestión de plantillas del CRM (operación diaria).
 * NO es mailing masivo ni WhatsApp Marketing: son mensajes de citas, avisos
 * al equipo y plantillas de tareas de seguimiento.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PantallaCargando } from '@/components/Cargando';
import {
  FileText, Mail, MessageCircle, ClipboardList, Save, X, RefreshCw,
  CheckCircle2, AlertTriangle, Zap, Pencil,
} from 'lucide-react';

export default function CrmPlantillas() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // full item
  const [form, setForm] = useState({ subject: '', body_text: '', html_body: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [waBusy, setWaBusy] = useState('');

  const load = useCallback(async () => {
    try {
      // seed/restore CRM ops templates (idempotent)
      try { await api.post('/tenant/crm/automation-templates/seed'); } catch { /* permission */ }
      const [mRes, tRes] = await Promise.all([
        api.get('/tenant/crm/message-templates'),
        api.get('/tenant/crm/task-templates').catch(() => ({ data: { items: [] } })),
      ]);
      setMessages(mRes.data?.items || []);
      setTaskTemplates(tRes.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar las plantillas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => {
    if (!item.installed || !item.id) {
      toast.message('Primero activa las plantillas del CRM');
      return;
    }
    setEditing(item);
    setForm({
      name: item.name || item.label || '',
      subject: item.subject || '',
      body_text: item.body_text || '',
      html_body: item.html_body || '',
    });
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      const payload = editing.channel === 'email'
        ? { name: form.name, subject: form.subject, html_body: form.html_body, text_body: form.body_text }
        : { body_text: form.body_text };
      await api.put(`/tenant/crm/message-templates/${editing.channel}/${editing.id}`, payload);
      toast.success('Plantilla guardada');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };


  const submitWaToMeta = async (item) => {
    if (!item?.id) return;
    setWaBusy(item.id);
    try {
      const { data } = await api.post(`/tenant/whatsapp/business/templates/${item.id}/submit`);
      toast.success(data.message || 'Enviada a Meta');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo enviar a Meta');
    } finally {
      setWaBusy('');
    }
  };

  const refreshWaMeta = async (item) => {
    if (!item?.id) return;
    setWaBusy(item.id);
    try {
      await api.post(`/tenant/whatsapp/business/templates/${item.id}/refresh`);
      toast.success('Estado actualizado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setWaBusy('');
    }
  };

  const reseed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/tenant/crm/automation-templates/seed');
      const n = (data.email || []).length + (data.whatsapp || []).length;
      toast.success(`Plantillas listas (${n})`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <PantallaCargando />;

  const emailItems = messages.filter(m => m.channel === 'email');
  const waItems = messages.filter(m => m.channel === 'whatsapp');

  return (
    <div className="space-y-6" data-testid="crm-plantillas">
      <div className="rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] p-4">
        <h2 className="text-base font-bold text-[#0F172A] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#F72585]" /> Plantillas del CRM
        </h2>
        <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
          Mensajes que el negocio manda <strong>por operación</strong> (citas, avisos al equipo)
          y listas de tareas listas para aplicar a un lead.
          {' '}<strong>No es mailing masivo</strong> — eso vive en Envíos de Email.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={reseed} disabled={seeding} data-testid="crm-plantillas-seed">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${seeding ? 'animate-spin' : ''}`} />
            {seeding ? 'Preparando…' : 'Asegurar plantillas del sistema'}
          </Button>
          <Link to="/tenant/email/plantillas"
            className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]">
            Ir a diseños de mailing →
          </Link>
          <Link to="/tenant/whatsapp/plantillas"
            className="inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-md border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100">
            Plantillas WhatsApp Business →
          </Link>
        </div>
      </div>

      {/* Mensajes email CRM */}
      <section className="space-y-3" data-testid="crm-msg-email">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#0F172A]" />
          <h3 className="text-sm font-semibold text-[#0F172A]">Correos de operación</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {emailItems.map(item => (
            <MessageCard key={`e-${item.stable_key}`} item={item} onEdit={() => openEdit(item)} />
          ))}
          {emailItems.length === 0 && (
            <p className="text-xs text-[#94A3B8]">No hay plantillas de email CRM. Pulsa «Asegurar plantillas».</p>
          )}
        </div>
      </section>

      {/* WhatsApp CRM */}
      <section className="space-y-3" data-testid="crm-msg-wa">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-[#0F172A]">WhatsApp de operación</h3>
        </div>
        <p className="text-[11px] text-[#94A3B8]">
          Las de utilidad Meta se crean en borrador; hay que aprobarlas en WhatsApp Business si aplica.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {waItems.map(item => (
            <MessageCard
              key={`w-${item.stable_key}`}
              item={item}
              onEdit={() => openEdit(item)}
              onSubmitMeta={() => submitWaToMeta(item)}
              onRefreshMeta={() => refreshWaMeta(item)}
              busy={waBusy === item.id}
            />
          ))}
        </div>
      </section>

      {/* Task templates */}
      <section className="space-y-3" data-testid="crm-task-templates-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#0F172A]" />
            <h3 className="text-sm font-semibold text-[#0F172A]">Plantillas de tareas</h3>
          </div>
          <Link to="/tenant/crm/tasks"
            className="text-xs font-medium text-[#F72585] hover:underline flex items-center gap-1">
            <Zap className="w-3 h-3" /> Crear / aplicar en Tareas
          </Link>
        </div>
        <p className="text-[11px] text-[#64748B]">
          Paquetes de seguimiento (ej. «Cliente nuevo»: llamar día 0, cotizar día 2…).
          Se aplican a un lead en un clic desde Tareas.
        </p>
        {taskTemplates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-white p-6 text-center">
            <p className="text-sm text-[#64748B]">Aún no hay plantillas de tareas.</p>
            <Link to="/tenant/crm/tasks" className="inline-block mt-2 text-xs font-semibold text-[#F72585] underline">
              Crear la primera en Tareas
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {taskTemplates.map(t => (
              <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3.5 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#0F172A]">{t.name}</p>
                  {t.description && <p className="text-[11px] text-[#94A3B8] mt-0.5">{t.description}</p>}
                  <p className="text-[11px] text-[#64748B] mt-1">
                    {(t.steps || []).length} tarea{(t.steps || []).length === 1 ? '' : 's'}
                    {(t.steps || []).length > 0 && (
                      <span className="text-[#94A3B8]"> · {(t.steps || []).map(s => s.title).filter(Boolean).slice(0, 3).join(' · ')}
                        {(t.steps || []).length > 3 ? '…' : ''}
                      </span>
                    )}
                  </p>
                </div>
                <Link to="/tenant/crm/tasks"
                  className="shrink-0 text-[11px] font-semibold rounded-full border border-[#E2E8F0] px-3 py-1.5 text-[#64748B] hover:text-[#0F172A]">
                  Usar
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" data-testid="crm-plantilla-editor">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-[#0F172A]">{editing.label || editing.name}</p>
                <p className="text-[11px] text-[#64748B]">{editing.purpose}</p>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-[#94A3B8] hover:text-[#0F172A]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {editing.channel === 'email' ? (
              <>
                <div>
                  <label className="text-[11px] font-medium text-[#64748B]">Nombre</label>
                  <Input className="mt-1 text-sm" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[#64748B]">Asunto</label>
                  <Input className="mt-1 text-sm" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} data-testid="crm-tpl-subject" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[#64748B]">HTML del correo</label>
                  <textarea
                    className="mt-1 w-full min-h-[220px] text-xs font-mono border border-[#E2E8F0] rounded-lg p-3"
                    value={form.html_body}
                    onChange={e => setForm(f => ({ ...f, html_body: e.target.value }))}
                    data-testid="crm-tpl-html"
                  />
                  <p className="text-[10px] text-[#94A3B8] mt-1">
                    Variables: {'{{lead_name}}'}, {'{{tenant_name}}'}, {'{{appointment_at}}'}, {'{{logo_url}}'}…
                  </p>
                </div>
              </>
            ) : (
              <div>
                <label className="text-[11px] font-medium text-[#64748B]">Texto del WhatsApp</label>
                <textarea
                  className="mt-1 w-full min-h-[160px] text-sm border border-[#E2E8F0] rounded-lg p-3"
                  value={form.body_text}
                  onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
                  data-testid="crm-tpl-wa-body"
                />
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  Si Meta ya la aprobó, un cambio de texto puede requerir nueva aprobación.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-[#0F172A] text-white" data-testid="crm-tpl-save">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageCard({ item, onEdit, onSubmitMeta, onRefreshMeta, busy }) {
  const ready = item.installed && item.status !== 'archived';
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col gap-2" data-testid={`crm-msg-${item.stable_key}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">{item.label}</p>
          <p className="text-[11px] text-[#64748B] mt-0.5 line-clamp-2">{item.purpose}</p>
        </div>
        {ready ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> Lista
          </span>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
            <AlertTriangle className="w-3 h-3" /> Pendiente
          </span>
        )}
      </div>
      {item.subject && (
        <p className="text-[11px] text-[#94A3B8] truncate">Asunto: {item.subject}</p>
      )}
      {item.body_text && item.channel === 'whatsapp' && (
        <p className="text-[11px] text-[#64748B] line-clamp-2 whitespace-pre-wrap">{item.body_text}</p>
      )}
      <div className="mt-auto pt-1 flex flex-col gap-1.5">
        <button type="button" onClick={onEdit} disabled={!ready}
          className="w-full text-xs font-semibold rounded-lg bg-[#0F172A] text-white px-3 py-2 hover:bg-[#1E293B] disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid={`crm-msg-edit-${item.stable_key}`}>
          <Pencil className="w-3 h-3" /> Editar mensaje
        </button>
        {item.channel === 'whatsapp' && ready && onSubmitMeta && item.status === 'draft' && (
          <button type="button" onClick={onSubmitMeta} disabled={busy}
            className="w-full text-xs font-semibold rounded-lg border border-emerald-300 text-emerald-800 bg-emerald-50 px-3 py-2 disabled:opacity-50"
            data-testid={`crm-msg-submit-meta-${item.stable_key}`}>
            {busy ? 'Enviando…' : 'Enviar a Meta (aprobación)'}
          </button>
        )}
        {item.channel === 'whatsapp' && ready && onRefreshMeta && (item.status === 'pending_approval' || item.external_status === 'PENDING') && (
          <button type="button" onClick={onRefreshMeta} disabled={busy}
            className="w-full text-[11px] font-medium text-[#64748B] underline disabled:opacity-50">
            Actualizar estado en Meta
          </button>
        )}
        {item.channel === 'whatsapp' && item.external_status === 'APPROVED' && (
          <p className="text-[10px] text-center text-emerald-700 font-semibold">Aprobada por Meta ✓</p>
        )}
      </div>
    </div>
  );
}
